import { z } from 'zod';
import { AGE_BANDS, GUEST_LIKELIHOODS, GUEST_SIDES, RSVP_STATUSES } from '../constants.js';
import {
  auditFields,
  emailSchema,
  isoDateSchema,
  isoDateTimeSchema,
  notesText,
  optionalText,
  shortText,
  tagsSchema,
  ulidSchema,
} from '../common.js';

export const addressSchema = z.object({
  line1: optionalText,
  line2: optionalText,
  city: optionalText,
  state: optionalText,
  postalCode: z.string().trim().max(20).optional(),
  country: z.string().trim().max(60).optional(),
});
export type Address = z.infer<typeof addressSchema>;

/**
 * One body at the wedding. A partner, a child and an unnamed "and guest" are all
 * Guest rows — there is no such thing as a plus-one string. That is what lets
 * every person be counted, given an RSVP and a meal, and seated: the seating
 * model addresses people by `guestId`, so anyone who is not a row cannot be sat
 * down or counted against a table's capacity.
 *
 * The mailing address and the invitation dates live on the Household, because one
 * envelope goes to one address.
 */
export const guestSchema = z.object({
  type: z.literal('Guest'),
  wedId: ulidSchema,
  guestId: ulidSchema,
  firstName: shortText,
  lastName: optionalText,
  email: emailSchema.optional(),
  phone: z.string().trim().max(40).optional(),
  side: z.enum(GUEST_SIDES).default('shared'),
  tags: tagsSchema,
  rsvp: z.enum(RSVP_STATUSES).default('pending'),
  /**
   * What the couple expects, as opposed to what the guest has said. Never set
   * by the guest's own RSVP form — it is the couple's private note to
   * themselves, and the public endpoints neither read nor write it.
   */
  likelihood: z.enum(GUEST_LIKELIHOODS).default('unsure'),
  ageBand: z.enum(AGE_BANDS).default('adult'),
  /**
   * An invitation that says "and guest" before anyone is named. Still a real row,
   * so it is counted in the anticipated headcount and can hold a seat; naming it
   * clears the flag and turns it into an ordinary guest.
   */
  isPlaceholder: z.boolean().default(false),
  /** Whose guest this is — lets the list read "Alex's guest". */
  placeholderFor: ulidSchema.optional(),
  /** Chosen on the public RSVP form, from the couple's `site.mealChoices`. */
  mealChoice: optionalText,
  /** When they answered for themselves, as opposed to the couple answering for them. */
  rsvpAt: isoDateTimeSchema.optional(),
  /** Free text; never sent to a model (docs/security.md: PII is stripped from AI context). */
  dietaryNotes: notesText,
  accessibilityNotes: notesText,
  notes: notesText,
  /** Every guest belongs to a household; a lone guest to one of their own. */
  householdId: ulidSchema,
  tableId: ulidSchema.optional(),
  seat: z.number().int().min(0).max(99).optional(),
  ...auditFields,
});
export type Guest = z.infer<typeof guestSchema>;

export const createGuestSchema = guestSchema
  .omit({
    type: true,
    wedId: true,
    guestId: true,
    createdAt: true,
    updatedAt: true,
    updatedBy: true,
  })
  .partial()
  .required({ firstName: true });
export type CreateGuestInput = z.infer<typeof createGuestSchema>;

export const updateGuestSchema = createGuestSchema.partial();
export type UpdateGuestInput = z.infer<typeof updateGuestSchema>;

/**
 * The invitation unit: the people who share one envelope and one address. A guest
 * who comes alone still has one, created for them and largely invisible.
 */
export const householdSchema = z.object({
  type: z.literal('Household'),
  wedId: ulidSchema,
  householdId: ulidSchema,
  label: shortText,
  address: addressSchema.optional(),
  notes: notesText,
  /** One envelope per household, so the dates belong here rather than per person. */
  invitationSentAt: isoDateSchema.optional(),
  thankYouSentAt: isoDateSchema.optional(),
  /**
   * When a gift arrived from this household, and what it was.
   *
   * On the household for the same reason the address is: a family gives one gift
   * and gets one card. Per person, a family of four would owe four thank-yous.
   *
   * The note is not decoration — you cannot write a thank-you that mentions the
   * gift without it, and "thank you for the lovely gift" is the sentence that
   * tells somebody you forgot. Deliberately free text and not a registry link:
   * half of what arrives at a wedding is cash, a cheque, or something somebody's
   * aunt made.
   *
   * No value field. Recording what each guest spent is a number that can only
   * ever embarrass somebody, and it is not needed to write the card.
   */
  giftReceivedAt: isoDateSchema.optional(),
  giftNote: z.string().trim().max(300).optional(),
  /**
   * The secret in this household's RSVP link. A capability: whoever holds it can
   * read and answer for this household and nothing else. Minted on demand,
   * rotatable, and absent until the couple asks for a link.
   */
  rsvpToken: z.string().min(16).max(64).optional(),
  /** Sent with the RSVP, by the family, in their own words. */
  songRequest: z.string().trim().max(500).optional(),
  guestMessage: z.string().trim().max(1000).optional(),
  ...auditFields,
});
export type Household = z.infer<typeof householdSchema>;

export const createHouseholdSchema = householdSchema
  .omit({
    type: true,
    wedId: true,
    householdId: true,
    createdAt: true,
    updatedAt: true,
    updatedBy: true,
  })
  .partial()
  .required({ label: true });
export type CreateHouseholdInput = z.infer<typeof createHouseholdSchema>;

export const updateHouseholdSchema = createHouseholdSchema.partial();
export type UpdateHouseholdInput = z.infer<typeof updateHouseholdSchema>;

/**
 * Adding a family in one go: the household plus everyone in it. Capped at 20 so
 * the household and its members always fit a single DynamoDB transaction.
 */
export const createHouseholdWithMembersSchema = createHouseholdSchema
  .partial({ label: true })
  .extend({
    /** Derived from the members when the couple does not care to name it. */
    label: shortText.optional(),
    side: z.enum(GUEST_SIDES).optional(),
    members: z
      .array(createGuestSchema.omit({ householdId: true }))
      .min(1)
      .max(20),
  });
export type CreateHouseholdWithMembersInput = z.infer<typeof createHouseholdWithMembersSchema>;

/** Sets one RSVP across a whole household — the common case for a family. */
export const householdRsvpSchema = z.object({
  rsvp: z.enum(RSVP_STATUSES),
  /** A placeholder nobody has named usually should not be marked attending. */
  includePlaceholders: z.boolean().default(false),
});
export type HouseholdRsvpInput = z.infer<typeof householdRsvpSchema>;

export const guestStatsSchema = z.object({
  /** Rows, placeholders included. */
  total: z.number().int(),
  named: z.number().int(),
  placeholders: z.number().int(),
  households: z.number().int(),
  byRsvp: z.record(z.enum(RSVP_STATUSES), z.number().int()),
  bySide: z.record(z.enum(GUEST_SIDES), z.number().int()),
  byAgeBand: z.record(z.enum(AGE_BANDS), z.number().int()),
  byTag: z.record(z.string(), z.number().int()),
  /** Said yes. What you tell the caterer to cook. */
  confirmedHeadcount: z.number().int(),
  /** Attending, maybe and pending — the worst case you must be able to seat. */
  anticipatedHeadcount: z.number().int(),
  /**
   * The same, minus the people you have marked unlikely. The number to plan
   * catering and a budget against, as opposed to the one you must be able to
   * seat if everybody surprises you.
   */
  expectedHeadcount: z.number().int(),
  byLikelihood: z.record(z.enum(GUEST_LIKELIHOODS), z.number().int()),
  /** Attending only, split the way a catering contract is priced. */
  attendingByAgeBand: z.record(z.enum(AGE_BANDS), z.number().int()),
  /** Households where nobody is still pending — the inverse of the chase list. */
  householdsFullyResponded: z.number().int(),
});
export type GuestStats = z.infer<typeof guestStatsSchema>;
